import {
    ButtonGradientPrimary,
    ButtonSecondary,
    Colors,
    Container,
    Heading,
    Icon,
} from '@amelio/ui-library';

import { InsightsPanel, type QuestionInsight } from './InsightsPanel';
import { QuickAssessmentGrid } from './QuickAssessmentGrid';
import { SurveyQuestion } from './SurveyQuestion';

import type { SurveyQuestionData } from '../types';
import type { ProficiencyLevel } from '@/types/competencies/ProficiencyLevel';
import type { User } from '@/types/directory/User';
import type { EmployeeCompetency } from '@/types/manager-assessment/Competency';
import type { ReactNode } from 'react';

import { useAmelioTranslations } from '@/hooks';

interface PerformanceSurveySectionProps {
    currentQuestion: SurveyQuestionData | undefined;
    onNext: () => void;
    onPrevious: () => void;
    selectedValues: string[];
    onAnswerChange: (values: string[]) => void;
    hasNext: boolean;
    hasPrevious: boolean;
    assessments: Record<string, number>;
    answerValues: Record<string, string[]>;
    reviewee?: User;
    currentQuestionIndex: number;
    totalQuestions: number;
    onDotClick: (index: number) => void;
    questionIds: string[];
    /** The current question's AI insight (three sections); undefined until generated. */
    insight?: QuestionInsight;
    /** The current question's error message when its generation failed. */
    insightError?: string;
    /** True while the current question's result is still pending after a generation run. */
    isInsightLoading?: boolean;
    /** Triggers AI insight generation for all questions. */
    onGenerateInsights?: () => void;
    /** Retries generation for just the current (failed) question. */
    onRetryInsights?: () => void;
    /** True while a generation run is in flight. */
    isGeneratingInsights?: boolean;
    /** False when generation can't run yet (e.g. the evaluation period isn't set); disables the button. */
    canGenerateInsights?: boolean;
    /** Tooltip explaining why generation is disabled. */
    insightsDisabledHint?: string;
    /** The assessed employee and the assessment, which the note under each question belongs to. */
    employeeId?: string;
    cycleInstanceId?: string;
    /** Whether the questions are the assessment's own, the only ones a note can be linked to. */
    takesQuestionNotes: boolean;
    otherText: string;
    onOtherTextChange: (value: string) => void;
    isQuickMode: boolean;
    onToggleMode: () => void;
    /**
     * Gates the quick assessment toggle (#13699). When false the button is not rendered at
     * all: hidden rather than disabled, so no greyed-out control or empty space remains.
     */
    canUseQuickAssessment?: boolean;
    /** When false the "Insights from engagement" section is not rendered. */
    canIncludeEngagement?: boolean;
    onEditCompetencies: () => void;
    /** When false the "Edit competencies" button is not rendered: the setup is locked once submitted (#16638). */
    canEditCompetencies?: boolean;
    competencies: EmployeeCompetency[];
    proficiencyLevels: ProficiencyLevel[];
    onRate: (rating: { competencyId: string; levelIndex: number }) => void;
    onSubmit: () => void;
    readOnly?: boolean;
    /**
     * Rendered in place of the question once the manager reaches the end of the survey, so the
     * completion screen keeps the section's card, title and actions instead of replacing them.
     */
    completion?: ReactNode;
    dataTestId?: string;
}

export const PerformanceSurveySection = ({
    currentQuestion,
    onNext,
    onPrevious,
    selectedValues,
    onAnswerChange,
    hasNext,
    hasPrevious,
    assessments,
    answerValues,
    reviewee,
    currentQuestionIndex,
    totalQuestions,
    onDotClick,
    questionIds,
    insight,
    insightError,
    isInsightLoading = false,
    onGenerateInsights,
    onRetryInsights,
    isGeneratingInsights = false,
    canGenerateInsights = true,
    insightsDisabledHint,
    employeeId,
    cycleInstanceId,
    takesQuestionNotes,
    otherText,
    onOtherTextChange,
    isQuickMode,
    onToggleMode,
    canUseQuickAssessment = true,
    canIncludeEngagement = true,
    onEditCompetencies,
    canEditCompetencies = true,
    competencies,
    proficiencyLevels,
    onRate,
    onSubmit,
    readOnly = false,
    completion,
    dataTestId = 'performance-survey-section',
}: PerformanceSurveySectionProps) => {
    const { t } = useAmelioTranslations();

    return (
        <div
            className={'col-to-row-xl flex w-full items-start gap-4 md:!gap-[26px]'}
            data-testid={dataTestId}
        >
            <Container
                borderRadius={'2xl'}
                className={
                    'flex w-full max-w-[842px] flex-1 flex-col border border-white/60 bg-white/60 shadow-sm backdrop-blur-md'
                }
            >
                <div className={'flex flex-1 flex-col gap-3 p-[16px] md:p-[26px]!'}>
                    <div
                        className={
                            'flex flex-col items-start justify-between gap-3 sm:flex-row sm:items-center sm:gap-0'
                        }
                    >
                        <Heading
                            level={5}
                            className={'!text-gray-900 md:!text-[20px]'}
                        >
                            {isQuickMode
                                ? t('managerAssessmentEvaluation.quickAssessment.title')
                                : t('managerAssessmentEvaluation.survey.title')}
                        </Heading>

                        <div
                            className={
                                'flex flex-wrap items-center justify-end gap-4 md:flex-nowrap'
                            }
                        >
                            {!readOnly && canEditCompetencies && (
                                <ButtonSecondary
                                    size={'sm'}
                                    onClick={onEditCompetencies}
                                    leftIcon={
                                        <Icon
                                            name={'settings-candle-2'}
                                            size={20}
                                        />
                                    }
                                    dataTestId={'edit-competencies-button'}
                                    className={'h-auto !scale-100 px-5 py-3 max-md:!min-w-60'}
                                >
                                    {t('managerAssessmentEvaluation.editCompetencies.button')}
                                </ButtonSecondary>
                            )}

                            {canUseQuickAssessment && (
                                <ButtonGradientPrimary
                                    size={'sm'}
                                    onClick={onToggleMode}
                                    leftIcon={
                                        <Icon
                                            name={'edit-edit-2'}
                                            size={20}
                                            color={Colors.WHITE}
                                        />
                                    }
                                    dataTestId={'assessment-mode-toggle'}
                                    className={'h-auto !scale-100 px-5 py-3 max-md:!min-w-60'}
                                >
                                    {isQuickMode
                                        ? t('managerAssessmentEvaluation.survey.title')
                                        : t('managerAssessmentEvaluation.quickAssessment.title')}
                                </ButtonGradientPrimary>
                            )}
                        </div>
                    </div>

                    {isQuickMode ? (
                        <QuickAssessmentGrid
                            competencies={competencies}
                            proficiencyLevels={proficiencyLevels}
                            assessments={assessments}
                            onRate={onRate}
                            onSubmit={onSubmit}
                            readOnly={readOnly}
                        />
                    ) : completion ? (
                        completion
                    ) : (
                        currentQuestion && (
                            <SurveyQuestion
                                question={currentQuestion.question}
                                reviewQuestion={currentQuestion.reviewQuestion}
                                reviewee={reviewee}
                                selectedValues={selectedValues}
                                onAnswerChange={onAnswerChange}
                                onNext={onNext}
                                onPrevious={onPrevious}
                                onSkip={onNext}
                                hasNext={hasNext}
                                hasPrevious={hasPrevious}
                                currentIndex={currentQuestionIndex}
                                totalQuestions={totalQuestions}
                                onDotClick={onDotClick}
                                answerValues={answerValues}
                                questionIds={questionIds}
                                employeeId={employeeId}
                                cycleInstanceId={cycleInstanceId}
                                takesQuestionNotes={takesQuestionNotes}
                                otherText={otherText}
                                onOtherTextChange={onOtherTextChange}
                                readOnly={readOnly}
                            />
                        )
                    )}
                </div>
            </Container>

            <InsightsPanel
                insight={insight}
                error={insightError}
                isLoading={isInsightLoading}
                onGenerate={onGenerateInsights}
                onRetry={onRetryInsights}
                isGenerating={isGeneratingInsights}
                generateDisabled={!canGenerateInsights || readOnly}
                includeEngagement={canIncludeEngagement}
                generateDisabledHint={
                    readOnly
                        ? t('managerAssessmentEvaluation.readOnly.message')
                        : insightsDisabledHint
                }
            />
        </div>
    );
};
